/**
 * Decision logic for the endless scroller, kept free of the DOM so it can be
 * unit tested. The hook measures the container and hands the numbers to these.
 */

/** Distance from an edge, in px, at which more months are loaded. */
export const EXTEND_MARGIN_TOP = 900;
export const EXTEND_MARGIN_BOTTOM = 1400;
/** Hard ceiling so an idle scroll can't grow the DOM forever (~10 years). */
export const MAX_SECTIONS = 120;
/** How close to the target counts as "already there", in px. */
export const AT_TARGET_TOLERANCE = 4;

/**
 * Which section is "the one you are reading"?
 *
 * The section crossing the reference line wins. If nothing crosses it — the
 * list is scrolled fully above or below — we fall back to the nearest section
 * on the correct side, so there is always an answer.
 *
 * @param {{key: string, top: number, bottom: number}[]} sections in visual order
 * @param {number} line viewport y of the top of the scroll area
 * @returns {string|null}
 */
export function pickActiveKey(sections, line) {
  let candidate = null;

  for (const section of sections) {
    if (section.top <= line && section.bottom > line) return section.key;
    if (section.bottom <= line) candidate = section.key; // last one fully above
    else if (!candidate) candidate = section.key; // first one below
  }

  return candidate;
}

/**
 * Should the list grow, and at which end?
 *
 * @param {{scrollTop: number, viewportHeight: number, contentHeight: number, sectionCount: number}} m
 * @returns {'start'|'end'|null}
 */
export function nextExtensionEdge({ scrollTop, viewportHeight, contentHeight, sectionCount }) {
  if (sectionCount >= MAX_SECTIONS) return null;
  if (scrollTop < EXTEND_MARGIN_TOP) return 'start';
  if (contentHeight - scrollTop - viewportHeight < EXTEND_MARGIN_BOTTOM) return 'end';
  return null;
}

/**
 * Where the container must scroll to put an element at the top of the view.
 * Clamped, because an element near the end cannot be scrolled all the way up.
 *
 * @param {number} scrollTop current position
 * @param {number} elementOffset element's distance from the top of the view
 * @param {number} maxScrollTop contentHeight - viewportHeight
 * @returns {number}
 */
export function scrollTargetFor(scrollTop, elementOffset, maxScrollTop) {
  return Math.min(Math.max(scrollTop + elementOffset, 0), Math.max(maxScrollTop, 0));
}

/**
 * Is the view already at that position? Used to grey out the Today button
 * without ever disabling it.
 *
 * @param {number} scrollTop
 * @param {number} target
 * @param {number} [tolerance]
 * @returns {boolean}
 */
export function isAtScrollTarget(scrollTop, target, tolerance = AT_TARGET_TOLERANCE) {
  return Math.abs(scrollTop - target) <= tolerance;
}
